/* eslint-disable no-underscore-dangle */
import searchMapHouseTemplate from '@templates/website/search/partials/_searchMapHouseTemplate.html.njk';
import type { LatLngBounds, LayerGroup, Map as LeafletMap, Marker } from 'leaflet';
import type L from 'leaflet';
import { LatLng } from 'leaflet';
import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import parseStringAsHtml from '@naturehouse/nh-essentials/lib/html/parser';
import Toast from '@naturehouse/design-system/components/atoms/toast/Toast';
import DestinationFilter from '../../../common/search/store/DestinationFilter';
import PaginationFilter from '../../../common/search/store/PaginationFilter';
import domEvents from '../../../events/domEvents';
import searchMapEvents from '../../../events/searchMapEvents';
import { animate, ANIMATIONS, fadeInAnimation, fadeOutAnimation } from '../../../ui/animations';
import MapsComponent from '../../listing/components/mapsComponent';
import GeoFilter from '../store/GeoFilter';
import HouseSearchFilters from '../store/HouseSearchFilters';
import HouseSearchListItems from '../store/HouseSearchListItems';
import PopularPageFilter from '../store/PopularPageFilter';
import SearchMapToLimitFilterConnector from '../storeConnectors/SearchMapToLimitFilterConnector';
import MapGroupedLocationsUpdater from '../MapGroupedLocationsUpdater';
import MapMarkerCreator from '../MapMarkerCreator';
import SearchMapTracking from './SearchMapTracking';

interface SearchMapBindingsInterface {
    zoomLevel: number;
    draggable: boolean;
}

export interface SearchMapContextInterface extends ContextInterface {
    element: HTMLElement;
    bindings: SearchMapBindingsInterface;
}

/**
 * @Deprecated on 13-11-2024
 *
 * Listed as deprecated after some necessary changes for LUM-11436.
 * It is no longer allowed to add new features or make other improvements, only bug fixing will be continued.
 */
export default class SearchMap extends MapsComponent implements Observer {
    public context!: SearchMapContextInterface;

    public map: LeafletMap | undefined = undefined;

    public Leaflet: typeof L | undefined = undefined;

    #filteredOnLatLon = false;

    readonly #latLonBounds?: LatLngBounds;

    #mapElement: HTMLElement | null = null;

    #layerGroup?: LayerGroup;

    #paginationUpdate = false;

    #houseDetailPlaceholder: HTMLElement | null = null;

    #searchOnMapButton: HTMLElement | null | undefined = undefined;

    #searchOnMapButtonIsHidden = true;

    readonly #houseResultsLimit = SearchMapToLimitFilterConnector.MAP_RESULTS_LIMIT;

    readonly #filters: HouseSearchFilters = HouseSearchFilters.getInstance();

    readonly #paginationFilter = this.#filters.get(PaginationFilter.NAME) as PaginationFilter;

    readonly #listItems: HouseSearchListItems = HouseSearchListItems.getInstance();

    readonly #searchMapTracking: SearchMapTracking = new SearchMapTracking();

    #initialZoomLevel = 5;

    readonly #translations: Record<string, string> = {};

    constructor(context: SearchMapContextInterface) {
        super(context);
        this.#filteredOnLatLon = false;

        this.#translations = context.element.dataset.translations
            ? JSON.parse(context.element.dataset.translations)
            : {};
    }

    public async onInit(): Promise<void> {
        this.#listItems.attach(this);

        this.#mapElement = document.querySelector('[data-role="search-map"]');
        this.#houseDetailPlaceholder = document.querySelector('[data-house-detail-placeholder]');
        this.#searchOnMapButton = this.context.element.parentElement?.querySelector(
            '[data-search-on-map-button]'
        );
        await this.initializeComponent();
    }

    public async update(): Promise<void> {
        const dialog: HTMLDialogElement | null = this.context.element.closest('dialog');

        if (dialog === null || dialog.open === false) {
            return;
        }

        if (
            this.map &&
            this.Leaflet &&
            this.#layerGroup &&
            this.#listItems.groupedLocations.length >= 1
        ) {
            await MapGroupedLocationsUpdater.updateMapGroupedLocations({
                groupedLocations: this.#listItems.groupedLocations,
                map: this.map,
                Leaflet: this.Leaflet,
                layerGroup: this.#layerGroup
            });

            const modalInstantiated = new CustomEvent(domEvents.templateInstantiated, {
                detail: this.context.element
            });

            document.dispatchEvent(modalInstantiated);
            return;
        }

        this.updateMapWithHouses();
    }

    private async initializeComponent(): Promise<void> {
        if (this.#listItems.houses.length <= this.#houseResultsLimit) {
            this.#paginationFilter.setPagination(
                Number(this.#paginationFilter.parameters.skip),
                this.#houseResultsLimit
            );
        }

        try {
            await this.initializeOrUpdateMap();
        } catch {
            // stub
        }
    }

    private async initializeOrUpdateMap(houses: HouseSearchListItem[] = []): Promise<void> {
        if (!this.Leaflet) {
            this.Leaflet = await this.initializeLeaflet();
            this.#layerGroup = new this.Leaflet.LayerGroup();
        }

        if (!this.map) {
            const tileLayers = this.getTileLayers();

            if (!tileLayers) {
                throw new Error('Cannot initialize map');
            }

            const leafletOptions = houses.length
                ? {
                      center: null,
                      zoom: this.context.bindings.zoomLevel
                  }
                : {
                      center: new LatLng(48.748945, 3.841229),
                      zoom: 5
                  };

            this.map = this.Leaflet.map(this.context.element.id, {
                center: leafletOptions.center || undefined,
                zoom: leafletOptions.zoom,
                minZoom: 2,
                maxZoom: 18,
                layers: [tileLayers.RoadMap]
            });

            this.#initialZoomLevel = this.map.getZoom();
            this.addControlPlaceholders(this.map, this.Leaflet);

            const verticalcenterright: any = 'verticalcenterright';
            this.map.zoomControl.setPosition(verticalcenterright);

            document.addEventListener(searchMapEvents.opened, () => {
                this.setEventListeners();
            });

            document.addEventListener(searchMapEvents.closed, () => {
                this.#paginationFilter.clear();
            });

            this.setEventListeners();
        }
    }

    private setEventListeners(): void {
        if (!this.map) {
            return;
        }

        const mapEventsCloseHouseDetailCard = ['click', 'zoomend'];
        mapEventsCloseHouseDetailCard.forEach((event) => {
            this.map?.on(event, () => this.closeHouseDetailCard());
        });

        this.map.on('zoomend', this.#handleZoomEndEvent);

        this.#searchOnMapButton?.addEventListener('click', this.#handleSearchOnMapButton);

        const mapEventsShowSearchForHousesButton = ['dragstart', 'zoomend'];

        mapEventsShowSearchForHousesButton.forEach((event) => {
            this.map?.on(event, () => this.showSearchForHousesButton());
        });
    }

    private addControlPlaceholders(map: any, leaflet: typeof L): void {
        const corners = map._controlCorners;
        const container = map._controlContainer;

        const classNameLeft = 'leaflet-verticalcenter leaflet-left';
        const classNameRight = 'leaflet-verticalcenter leaflet-right';

        corners.verticalcenterleft = leaflet.DomUtil.create('div', classNameLeft, container);
        corners.verticalcenterright = leaflet.DomUtil.create('div', classNameRight, container);
    }

    private updateMapWithHouses(): void {
        if (!this.map) {
            return;
        }

        const { houses } = this.#listItems;
        this.#layerGroup?.clearLayers();

        const markerData = this.getHouseMarkers(houses);
        const markers = markerData.map(([marker]) => marker);

        if (markerData.length === 0) {
            this.#showNoResultsToast();
            return;
        }

        for (const [marker, house] of markerData) {
            this.#layerGroup?.addLayer(marker);
            this.#setupMarker(marker, house);
        }

        this.#layerGroup?.addTo(this.map);
        this.removeMapLoadingState();
        this.#hideNoResultsToast();

        if (markerData.length === 1) {
            const [marker, house] = markerData[0];
            this.openHouseDetailCard(marker, house);
        }

        if (houses.length > 0 && (this.#paginationUpdate || !this.#filteredOnLatLon)) {
            this.centerMap(markers);
        }
    }

    #setupMarker(marker: Marker, house: HouseSearchListItem): void {
        const houseDetailCard = this.#createHouseDetailCard(house);

        if (!houseDetailCard) {
            return;
        }

        marker.addEventListener('click', (): void => {
            this.openHouseDetailCard(marker, house);
        });

        // @ts-ignore - this is used for testing purposes
        if (marker._icon) {
            // @ts-ignore
            marker._icon.id = `marker-${house.id}`;
        }
    }

    #createHouseDetailCard(house: HouseSearchListItem): HTMLElement | null {
        const templateData = {
            house,
            translations: this.#translations
        };

        const houseDetailCard = parseStringAsHtml(
            searchMapHouseTemplate.render(templateData),
            'div'
        );

        const closeButton = houseDetailCard.querySelector('[data-close-house-detail-card]');
        closeButton?.addEventListener('click', async (): Promise<void> => {
            this.closeHouseDetailCard();
        });

        return houseDetailCard;
    }

    private async showSearchForHousesButton(): Promise<void | string> {
        if (!this.#searchOnMapButtonIsHidden || !this.#searchOnMapButton) {
            return Promise.resolve();
        }

        this.#hideNoResultsToast();

        this.#searchOnMapButton?.removeAttribute('hidden');
        fadeInAnimation(this.#searchOnMapButton);
        this.#searchOnMapButtonIsHidden = false;

        return Promise.resolve();
    }

    private hideSearchForHousesButton(): void {
        if (this.#searchOnMapButtonIsHidden || !this.#searchOnMapButton) {
            return;
        }

        fadeOutAnimation(this.#searchOnMapButton);
        this.#searchOnMapButtonIsHidden = true;
        this.#searchOnMapButton?.setAttribute('hidden', '');
    }

    private openHouseDetailCard(marker: Marker, house: HouseSearchListItem): void {
        this.hideSearchForHousesButton();
        this.#hideNoResultsToast();
        this.centerMap([marker]);

        const template = this.#createHouseDetailCard(house);

        if (!this.#houseDetailPlaceholder || !template) {
            return;
        }

        this.#houseDetailPlaceholder.innerHTML = '';
        this.#houseDetailPlaceholder.append(template);
        this.#searchMapTracking.trackMapViewHousePreviewEvent(house.id);

        animate(this.#houseDetailPlaceholder, ANIMATIONS.SLIDE_AND_FADE_IN);
    }

    private closeHouseDetailCard(): void {
        if (!this.#houseDetailPlaceholder) {
            return;
        }

        animate(this.#houseDetailPlaceholder, ANIMATIONS.SLIDE_AND_FADE_OUT);
        this.#houseDetailPlaceholder.innerHTML = '';
    }

    private centerMap(markers: Marker[]): void {
        if (!this.map) {
            return;
        }

        if (markers.length === 1) {
            this.map.setView(markers[0].getLatLng(), this.context.bindings.zoomLevel);
        }

        if (markers.length > 1 && this.Leaflet) {
            const featureGroup = new this.Leaflet.FeatureGroup(markers);
            this.map.fitBounds(featureGroup.getBounds());

            this.#paginationUpdate = false;
            this.#filteredOnLatLon = false;
        }
    }

    readonly #showNoResultsToast = (): void => {
        this.closeHouseDetailCard();
        this.hideSearchForHousesButton();
        this.#hideNoResultsToast();

        if (!this.#mapElement) {
            return;
        }

        const toast: Toast = new Toast(this.#translations.noResults);
        this.#mapElement.appendChild(toast);
        this.removeMapLoadingState();
    };

    readonly #hideNoResultsToast = (): void => {
        const existingToast = this.#mapElement?.querySelector('nh-toast');
        existingToast?.remove();
    };

    readonly #handleSearchOnMapButton = (): void => {
        this.closeHouseDetailCard();
        this.#layerGroup?.clearLayers();

        this.#onMapChange();
        this.#searchMapTracking.trackMapSearchAreaEvent();
    };

    readonly #onMapChange = (): void => {
        if (!this.map) {
            return;
        }

        this.addMapLoadingState();
        this.hideSearchForHousesButton();

        const latLonBounds = this.map.getBounds();

        if (
            this.#latLonBounds === latLonBounds ||
            latLonBounds.getSouth() === latLonBounds.getNorth() ||
            latLonBounds.getWest() === latLonBounds.getEast()
        ) {
            return;
        }

        const geoBox = Object.values({
            top: latLonBounds.getNorth(),
            left: latLonBounds.getWest(),
            bottom: latLonBounds.getSouth(),
            right: latLonBounds.getEast()
        }).join(',');

        const destinationFilter = this.#filters.get(DestinationFilter.NAME) as DestinationFilter;

        const geoFilter = this.#filters.get(GeoFilter.NAME) as GeoFilter;

        const popularFilter = this.#filters.get(PopularPageFilter.NAME) as PopularPageFilter;

        destinationFilter.clear();
        popularFilter.clear();

        geoFilter.geobox = geoBox;

        setLocationField('#search_destination_in_modal', '');

        function setLocationField(id: string, value: string): void {
            const field: HTMLInputElement = document.querySelector(id) as HTMLInputElement;

            if (field === null) {
                return;
            }

            field.value = value;
            field.dispatchEvent(new Event('change'));
        }

        this.#filteredOnLatLon = true;
    };

    private removeMapLoadingState(): void {
        this.context.element.classList.remove('loading-spinner');
    }

    private addMapLoadingState(): void {
        this.context.element.classList.add('loading-spinner');
    }

    private getHouseMarkers(houses: HouseSearchListItem[]): [Marker, HouseSearchListItem][] {
        if (!this.Leaflet) {
            return [];
        }

        const markers: [Marker, HouseSearchListItem][] = [];

        houses.forEach((house) => {
            if (!house.latitude && !house.longitude) {
                return;
            }

            const marker: Marker = MapMarkerCreator.createLeafletMarker({
                lat: house.latitude || 0,
                lon: house.longitude || 0,
                draggable: Boolean(this.context.bindings.draggable)
            });

            markers.push([marker, house]);
        });

        return markers;
    }

    readonly #handleZoomEndEvent = (): void => {
        this.#initialZoomLevel = this.map?.getZoom() || this.#initialZoomLevel;
    };

    static getClassName(): string {
        return 'SearchMap';
    }
}
